import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { GridColDef } from '@mui/x-data-grid';
import { RootState } from '../../../store';
import ChartPanel from '../components/ChartPanel';
import KpiCard from '../components/KpiCard';
import DataGridTable from '../../Shared/DataGridTable/DataGridTable';
import { CallLogRow, scoreBand } from '../../../store/callStats/callStatsTypes';
import { bandColor, CHART_COLORS } from '../components/scoreColors';
import { customError, darkPurple, lightTeal, orange } from '../../../mainTheme';

const fmtDur = (sec: number) => {
  const m = Math.floor(sec / 60);
  return m > 0 ? `${m}m ${sec % 60}s` : `${sec}s`;
};

const columns: GridColDef[] = [
  {
    field: 'startedAt',
    headerName: 'Started',
    flex: 1.3,
    minWidth: 150,
    valueFormatter: ({ value }) =>
      moment(value as string).format('MMM D, HH:mm'),
  },
  {
    field: 'type',
    headerName: 'Type',
    flex: 0.6,
    minWidth: 80,
    valueFormatter: ({ value }) => String(value).toUpperCase(),
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    minWidth: 120,
    valueFormatter: ({ value }) => String(value).replace('_', ' '),
  },
  {
    field: 'durationSec',
    headerName: 'Duration',
    flex: 0.8,
    minWidth: 90,
    valueFormatter: ({ value }) => fmtDur(value as number),
  },
  {
    field: 'experienceScore',
    headerName: 'Exp.',
    flex: 0.6,
    minWidth: 80,
    renderCell: (p) =>
      p.value == null ? (
        '—'
      ) : (
        <Box
          sx={{
            color: bandColor[scoreBand(p.value as number)],
            fontWeight: 600,
          }}
        >
          {Math.round(p.value as number)}
        </Box>
      ),
  },
  {
    field: 'mos',
    headerName: 'MOS',
    flex: 0.6,
    minWidth: 70,
    valueFormatter: ({ value }) =>
      value == null ? '—' : (value as number).toFixed(2),
  },
];

const PairSection: React.FC = () => {
  const { pair } = useSelector((state: RootState) => state.callStats);
  const p = pair.data;
  const rows = (p?.calls ?? []).map((c: CallLogRow) => ({
    id: c.callId,
    ...c,
  }));

  return (
    <ChartPanel
      title="Calls between two users"
      subtitle={
        p
          ? `${p.userA.displayName}  ↔  ${p.userB.displayName}`
          : 'Select User A and User B'
      }
      loading={pair.loading}
      error={pair.error}
      empty={!pair.loading && (!p || p.totalCalls === 0)}
      emptyText="No calls between these two users in this range"
      mocked={pair.mocked}
      minHeight={340}
    >
      <Box width="100%">
        <Grid container spacing={1.5} mb={1.5}>
          <Grid item xs={6} sm={3}>
            <KpiCard
              label="Total"
              value={p?.totalCalls ?? '—'}
              accent={darkPurple}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KpiCard
              label="Video"
              value={p?.video ?? '—'}
              accent={CHART_COLORS[0]}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KpiCard label="SIP" value={p?.sip ?? '—'} accent={lightTeal} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KpiCard
              label="Failed"
              value={p?.failed ?? '—'}
              accent={customError}
            />
          </Grid>
        </Grid>
        <Typography variant="caption" sx={{ color: orange }}>
          Avg duration {p ? fmtDur(p.avgDurationSec) : '—'}
        </Typography>
        <DataGridTable
          rows={rows}
          columns={columns}
          pageSize={5}
          minHeight="240px"
          maxHeight="300px"
        />
      </Box>
    </ChartPanel>
  );
};

export default PairSection;
